/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV6d - DIE REGELN DER LÜFTUNGSDATEN-SEITEN, OHNE REACT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anlagenliste, Kreislaufverbund, Herstellerunterlagen, Datenblattimporte.
 * Was hier steht, stand vor TV6d in den Seiten selbst (Wortlaute, Zuordnung
 * Zustand → Wort und Farbe, das Lesen der Bedingungen eines Betriebspunkts,
 * das Bilden der Auswahl). Es ist herausgelöst, damit es ohne Seite prüfbar ist
 * (`datenregeln.test.ts`); geändert ist es nicht - die Charakterisierung
 * `Lueftungsdaten.schreibwege.test.tsx` hält die Aufrufe fest.
 */
import type { Importauswahl, Importdetail, Importpunkt } from '../../api';
import { formatiereZahl, type Vorbehalt, type Zustand } from '../../muster';

// ═══ Herstellerunterlagen (Datenblätter je Bauteiltyp) ══════════════════════

/** Zustand eines Datenblatts → Wort. */
export const DATENBLATT_ZUSTAND: Record<string, string> = {
  offen: 'offen',
  wartet: 'wartet',
  laeuft: 'läuft',
  gesucht: 'Datei da',
  gefunden: 'Vorschläge',
  nicht_gefunden: 'nichts gefunden',
  geprueft: 'geprüft',
};

/** Zustand eines Datenblatts → Farbe. Vor TV6d „laufend“ für wartet/läuft; im Muster heißt das `info`. */
export function datenblattZustand(z: string): Zustand {
  switch (z) {
    case 'geprueft': return 'gut';
    case 'laeuft': case 'wartet': return 'info';
    case 'nicht_gefunden': return 'warnung';
    default: return 'neutral';
  }
}

/** Was der Stand des Abgleichs je Zustand heißt. */
export const ABGLEICH_ZUSTAND: Record<string, string> = {
  unveraendert: 'unverändert',
  menge_geaendert: 'Luftmenge geändert',
  neu: 'neu, noch nicht abgeglichen',
  typ_gewechselt: 'Typ gewechselt',
  entfernt: 'aus dem Modell entfernt',
  datenblatt_fehlt: 'kein Datenblatt zum Typ',
  ohne_menge: 'ohne Luftmenge',
  ohne_datenblatt: 'ohne Datenblatt',
};

/** Die Zustände, bei denen etwas nachzuführen ist (vor TV6d farbig hinterlegt). */
export const ABGLEICH_HANDELN = new Set(['menge_geaendert', 'neu', 'typ_gewechselt', 'entfernt']);

/** Ob alle Bauteile eines Typs Werte aus dem Datenblatt tragen - dann grün; eine halbe Zuweisung bleibt ungefärbt. */
export function vollstaendigZugewiesen(z: { bauteile: number; zugewiesen: number }): boolean {
  return z.bauteile > 0 && z.zugewiesen >= z.bauteile;
}

/**
 * Wie lange die Warteschlange der Auswertung noch braucht: zwei je Durchlauf,
 * alle 45 Sekunden (Wortlaut und Rechnung wie vor TV6d).
 */
export function warteschlangeText(inArbeit: number): string {
  return `${formatiereZahl(inArbeit, 0)} Auswertung(en) in der Warteschlange. Zwei je Durchlauf, alle 45 Sekunden - etwa `
    + `${formatiereZahl(Math.ceil(((inArbeit / 2) * 1.75) / 60), 0)} Stunde(n). Die Seite lädt sich selbst nach.`;
}

// ═══ Datenblattimporte (JSON des Herstellers) ═══════════════════════════════

/** Zustand einer Zuordnung je Bauteiltyp → Wort. */
export const ZUORDNUNG_ZUSTAND: Record<string, string> = {
  zugewiesen: 'zugewiesen',
  teilweise: 'teilweise',
  veraltet: 'Menge geändert',
  fehler: 'Fehler',
  offen: 'offen',
};

export function zuordnungZustand(z: string): Zustand {
  switch (z) {
    case 'zugewiesen': return 'gut';
    case 'veraltet': case 'fehler': return 'warnung';
    default: return 'neutral';
  }
}

/** Die gespeicherte Auswahl einer früheren Zuweisung (JSON im Datenblatt), oder null. */
export interface AlteAuswahl {
  sizeKey?: string | null;
  tabelle?: string | null;
  bedingungen?: Record<string, number> | null;
}

export function alteAuswahl(auswahl: string | null | undefined): AlteAuswahl | null {
  if (!auswahl) return null;
  try {
    return JSON.parse(auswahl) as AlteAuswahl;
  } catch {
    return null;
  }
}

/** Welche Merkmale das Dokument verlangt - aus den `selection`-Angaben seiner bedingten Zuordnungen. */
export function verlangteMerkmale(detail: Importdetail): Set<string> {
  return new Set(detail.zuordnungen.conditional.flatMap((z) => (z.selection ?? '').split('+')).filter(Boolean));
}

/** Die Merkmale, die der Anwender selbst wählt (`userChoice` mit erlaubten Werten). */
export function eigeneMerkmale(detail: Importdetail) {
  return detail.zuordnungen.conditional.filter((z) => z.selection === 'userChoice' && z.allowedValues);
}

/** Die Eingaben der Zuordnung, aus denen die Auswahl entsteht. */
export interface Merkmalwahl {
  sizeKey: string;
  variantKey: string;
  tabelle: string;
  punktIndex: number | null;
  konfiguration: string;
  eigeneWahl: Record<string, string>;
}

/**
 * Die Auswahl, wie sie an Vorschau und Übernahme geht. Maße und Variante kommen
 * aus der gewählten Größe, eine eigens gewählte Variante hat Vorrang.
 */
export function auswahlAus(detail: Importdetail | null, w: Merkmalwahl): Importauswahl {
  const g = detail?.groessen.find((x) => x.sizeKey === w.sizeKey);
  return {
    sizeKey: w.sizeKey || null,
    variantKey: w.variantKey || g?.variantKey || null,
    widthMm: g?.breite ?? null,
    heightMm: g?.hoehe ?? null,
    housingLengthMm: g?.gehaeuselaenge ?? null,
    tabelle: w.tabelle || null,
    punktIndex: w.punktIndex,
    konfiguration: w.konfiguration || null,
    eigeneWahl: w.eigeneWahl,
  };
}

/**
 * Die Bedingungen für den Abgleich je Bauteil: die des gewählten Punkts
 * („spalt 12 mm“ → `{ spalt: 12 }`); ist keiner gewählt oder trägt er keine,
 * die der letzten Zuweisung - beim Aktualisieren ändert sich die Spaltbreite
 * nicht, nur die Luftmenge.
 */
export function abgleichBedingungen(punkt: Importpunkt | undefined, alt: AlteAuswahl | null): Record<string, number> {
  let bedingungen: Record<string, number> = {};
  for (const b of punkt?.bedingungen ?? []) {
    const m = b.match(/^(\S+)\s+(-?[\d.]+)/);
    if (m) bedingungen[m[1]] = Number(m[2]);
  }
  if (Object.keys(bedingungen).length === 0 && alt?.bedingungen) bedingungen = alt.bedingungen;
  return bedingungen;
}

/** Der Text eines Bauteiltyps in der Auswahl: Typ, Zahl der Bauteile, wie viele eine Luftmenge tragen, Familie. */
export function typText(t: { familie: string; typ: string; bauteile: number; mitLuftmenge: number }): string {
  return `${t.typ} — ${formatiereZahl(t.bauteile, 0)} Bauteil(e)`
    + (t.mitLuftmenge < t.bauteile ? `, ${formatiereZahl(t.mitLuftmenge, 0)} mit Luftmenge` : ', alle mit Luftmenge')
    + ` · ${t.familie}`;
}

/** Schlüssel eines Bauteiltyps im Auswahlfeld (Familie und Typ, getrennt durch NUL wie vor TV6d). */
export const typSchluessel = (familie: string, typ: string) => `${familie}\u0000${typ}`;

/** Eine Spanne „von – bis“ (oder ein Wert), deutsch. */
export function spanne(von: number | null | undefined, bis: number | null | undefined, nachkomma = 0): string {
  if (von === null || von === undefined) return '';
  return von === bis || bis === null || bis === undefined
    ? formatiereZahl(von, nachkomma)
    : `${formatiereZahl(von, nachkomma)} – ${formatiereZahl(bis, nachkomma)}`;
}

// ═══ Kreislaufverbund ═══════════════════════════════════════════════════════

/**
 * Die Vorbehalte der KVS-Seite. Beide Sätze standen vor TV6d im Kopf der Seite
 * und über der Anlagentabelle; nach S1.2 gehören sie direkt unter den Kopf.
 */
export function kvsVorbehalte(): Vorbehalt[] {
  return [
    {
      art: 'sonstiges',
      text: 'Die Typdatenblätter nennen Plätze, Volumenströme und Leistungen, aber nicht, welches körperliche Gerät auf '
        + 'welchem Platz sitzt. Das Bild zeigt deshalb keine Gerätezuordnung.',
    },
    {
      art: 'sonstiges',
      text: 'Welche Anlage an welcher KVS-Instanz hängt, geht aus den Typdatenblättern nicht hervor; unter „Angeschlossene '
        + 'Anlagen“ stehen deshalb alle Anlagen der Gruppe.',
    },
  ];
}

/** Ein Gerätetyp in einer Zeile: Variante, Angebot und die Kennwerte des Typdatenblatts. */
export function typdatenblattText(t: {
  variante: string | null; angebotsnummer: string; volumenstromM3h: number | null; ventilatorAnzahl: number | null;
  ventilatorLeistungKw: number | null; sfpWsM3: number | null; euroventWinter: string | null;
}): string {
  return [
    t.variante,
    `Angebot ${t.angebotsnummer}`,
    t.volumenstromM3h !== null ? `${formatiereZahl(t.volumenstromM3h, 0)} m³/h` : null,
    t.ventilatorAnzahl ? `${formatiereZahl(t.ventilatorAnzahl, 0)} Ventilatoren` : null,
    t.ventilatorLeistungKw ? `${formatiereZahl(t.ventilatorLeistungKw, 2)} kW` : null,
    t.sfpWsM3 ? `SFP ${formatiereZahl(t.sfpWsM3, 0)} Ws/m³` : null,
    t.euroventWinter ? `Eurovent ${t.euroventWinter}` : null,
  ].filter(Boolean).join(' · ');
}

// ═══ Anlagenliste ═══════════════════════════════════════════════════════════

/** Luftstrom-Kürzel → Wort (Vorschau der Herstellerauslegung). */
export const LUFTSTROM: Record<string, string> = { ZU: 'Zuluft', AB: 'Abluft', AU: 'Außenluft', FO: 'Fortluft' };

/** Ein Gerät, das kein RLT-Gerät ist (z. B. eine Hydraulikstation): Anlagenkapazität ≠ Gehäuseluftstrom. */
export function istHydraulik(produktklasse: string | null | undefined): boolean {
  return !!produktklasse && produktklasse !== 'air_handling_unit';
}
